import type { ScheduledPostDto, SocialConnection } from "@nlc/shared-types";

import { USER_ROLES } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import type { UserRole } from "@/features/auth/permissions/config";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import { toast } from "@/components/toast/toast-queue";
import { FeaturePageLayout } from "@/features/shared/layout";

import type { InsightsView, PublishTab } from "../publishSearch";

import { useDisconnectMeta } from "../hooks/useDisconnectMeta";
import { InsightsTab } from "./insights/InsightsTab";
import { PostInsightsPage } from "./insights/post-details/PostInsightsPage";
import { MetaAccountsTable } from "./MetaAccountsTable";
import { ScheduleWizard } from "./schedule-wizard/ScheduleWizard";
import { ScheduleCalendar } from "./schedule/ScheduleCalendar";

const CUSTOMER_ROLES = [
  USER_ROLES.OWNER,
  USER_ROLES.ADMIN,
  USER_ROLES.COLLABORATOR,
] as UserRole[];

const TABS: Array<{ id: PublishTab; label: string }> = [
  { id: "schedule", label: "Schedule" },
  { id: "insights", label: "Insights" },
  { id: "settings", label: "Settings" },
];

export function PublishPage({
  connection,
  isConnecting,
  onConnect,
  onLinkPages,
  postId,
  tab = "schedule",
  view,
}: {
  connection: SocialConnection | undefined;
  isConnecting: boolean;
  onConnect: () => void;
  onLinkPages: () => void;
  postId?: number;
  tab?: PublishTab;
  view?: InsightsView;
}) {
  const navigate = useNavigate();
  const [isDisconnectOpen, setIsDisconnectOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState<ScheduledPostDto>();
  const disconnect = useDisconnectMeta();

  function confirmDisconnect() {
    disconnect.mutate(undefined, {
      onError: () => {
        toast.add(
          { title: "Could not disconnect your Meta account", type: "error" },
          { timeout: 5000 },
        );
      },
      onSettled: () => setIsDisconnectOpen(false),
    });
  }

  function selectTab(nextTab: PublishTab) {
    navigate({ search: { tab: nextTab }, to: "/publish" });
  }

  function closeWizard() {
    setIsWizardOpen(false);
    setPostToEdit(undefined);
  }

  if (isWizardOpen || postToEdit) {
    return (
      <div className="p-xl">
        <ScheduleWizard
          onClose={closeWizard}
          onGoToSettings={() => {
            closeWizard();
            selectTab("settings");
          }}
          postToEdit={postToEdit}
        />
      </div>
    );
  }

  return (
    <div className="p-xl">
      <FeaturePageLayout
        createButton={{
          label: "New schedule",
          onClick: () => setIsWizardOpen(true),
          requiredRoles: CUSTOMER_ROLES,
        }}
        tabs={{
          onSelectionChange: (key) => selectTab(key as PublishTab),
          selectedKey: tab,
          tabs: TABS,
        }}
        title="Publish"
      >
        {tab === "schedule" && (
          <ScheduleCalendar
            onEditPost={setPostToEdit}
            onManageAccounts={() => selectTab("settings")}
            onSchedulePost={() => setIsWizardOpen(true)}
          />
        )}
        {tab === "insights" &&
          (postId ? (
            <PostInsightsPage postId={postId} />
          ) : (
            <InsightsTab view={view} />
          ))}
        {tab === "settings" && (
          <div className="flex flex-col gap-lg p-xl">
            <div className="flex items-center justify-between gap-md">
              <h2 className="text-lg font-semibold text-text-primary">
                Meta accounts
              </h2>
              <div className="flex gap-md">
                {connection?.connected ? (
                  <>
                    {connection.hasErrors && (
                      <Button
                        isLoading={isConnecting}
                        onPress={onConnect}
                        variant="secondary"
                      >
                        Reconnect
                      </Button>
                    )}
                    <Button
                      color="destructive"
                      onPress={() => setIsDisconnectOpen(true)}
                      variant="secondary"
                    >
                      Disconnect
                    </Button>
                    <Button onPress={onLinkPages}>Link pages</Button>
                  </>
                ) : (
                  <Button isLoading={isConnecting} onPress={onConnect}>
                    Connect Meta account
                  </Button>
                )}
              </div>
            </div>
            <MetaAccountsTable />
          </div>
        )}
      </FeaturePageLayout>

      <ConfirmationModal
        confirmButtonText="Disconnect"
        isConfirmLoading={disconnect.isPending}
        isOpen={isDisconnectOpen}
        onConfirm={confirmDisconnect}
        onOpenChange={setIsDisconnectOpen}
        text="The pages you linked to brands will be removed for everyone in this project."
        title="Disconnect Meta account"
      />
    </div>
  );
}
